/**
 * DraggableCard - Wrapper component for drag-and-drop card functionality
 *
 * Features:
 * - useSortable integration from @dnd-kit
 * - Edit button overlay (top-right)
 * - Drag handle indicator (left side)
 * - Jiggle animation on hover
 * - Visual feedback during drag
 * - Renders children directly when not in edit mode
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEditMode } from '../../contexts/EditModeContext';
import './DraggableCard.css';

const DraggableCard = ({ id, children, onEditClick }) => {
  const { isEditMode } = useEditMode();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // If not in edit mode, render children directly (no wrapper)
  if (!isEditMode) {
    return children;
  }

  // In edit mode, wrap with draggable functionality
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`draggable-card ${isDragging ? 'dragging' : ''}`}
      {...attributes}
    >
      {/* Drag Handle Indicator (left side) */}
      <div className="drag-handle" {...listeners} title="Drag to reorder">
        <span className="drag-icon">⋮⋮</span>
      </div>

      {/* Edit Button Overlay (top-right) */}
      <button
        className="edit-button"
        onClick={() => onEditClick && onEditClick(id)}
        title="Edit card configuration"
      >
        ✏️ Edit
      </button>

      {/* Card Content */}
      <div className="card-content">
        {children}
      </div>
    </div>
  );
};

DraggableCard.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onEditClick: PropTypes.func
};

export default DraggableCard;
